/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { VNode } from 'preact';
import { useRef, useState } from 'preact/hooks';
import { Dimension } from '../../utils/UNSAFE_visTypes/common';
import { Scale } from '../../utils/UNSAFE_visTypes/chart';
import { getResolvedDragMode, getCursorType, getMarqueeDims } from './utils';
import { isMobile } from '../../utils/UNSAFE_visUtils/clientUtils';
import { useDragModeControls } from './useDragModeControls';
import { Marquee } from './Marquee';
import { SelectionDetail } from '../PRIVATE_useVisSelection/useSelection';

type MarqueeProps<K, D> = {
  chartWidth: number;

  plotAreaSpace: Dimension;

  isZoomScroll: boolean;

  isMultiSelection: boolean;

  onSelectionChange?: (detail: SelectionDetail<K, D>) => void;

  xScale: Scale;

  onViewportChange?: (x: { xMin?: number; yMin?: number; xMax?: number; yMax?: number }) => void;

  dragMode: 'user' | 'select' | 'zoom' | 'pan' | 'off';

  getDataById: (id: K) => D | undefined;

  xAxisExtent: { min: number; max: number };

  selectedIds?: Set<K>;

  viewportMin: number;

  viewportMax: number;

  isRtl: boolean;
};

export function useChartMarquee<K, D>({
  plotAreaSpace,
  dragMode,
  chartWidth,
  isMultiSelection,
  onViewportChange,
  isZoomScroll,
  xAxisExtent,
  xScale,
  selectedIds,
  getDataById,
  onSelectionChange,
  viewportMax,
  viewportMin,
  isRtl
}: MarqueeProps<K, D>) {
  const isHovered = useRef(false);

  const numOfActiveTouches = useRef(0);

  const initialPointRef = useRef<{
    x: number;
    y: number;
    isMeta?: boolean;
  }>();

  const { dragButtonDivRef, toggleButtonContent, userDragMode } = useDragModeControls({
    chartWidth,
    plotAreaSpace,
    isRtl,
    isZoomScroll,
    isMultiSelection,
    dragMode
  });

  // for drag --> dragging cursor rerender in pan mode
  const [isPointerDown, setIsPointerDown] = useState(false);

  const [finalPoint, setFinalPoint] = useState<{ x: number; y: number }>();

  const selectedIdsRef = useRef<Set<K>>(new Set());
  const isDeviceMobile = isMobile();

  const resolvedDragMode = getResolvedDragMode(
    isZoomScroll,
    isMultiSelection,
    isDeviceMobile,
    dragMode
  );

  const mode = resolvedDragMode != 'off' ? userDragMode || resolvedDragMode : 'off';

  const dims = getMarqueeDims(xScale, initialPointRef.current, finalPoint);

  const onPointerEnter = () => {
    isHovered.current = true;
  };

  const onPointerDown = (event: PointerEvent) => {
    // prevent setIsPointerDown(true) when clicked on button
    // otherwise the button will disapper before being clicked.
    if (dragButtonDivRef.current?.contains(event.target as HTMLElement)) {
      return;
    }

    const x = event.offsetX;
    const y = event.offsetY;

    setIsPointerDown(true);

    if (
      x < plotAreaSpace.x ||
      x > plotAreaSpace.x + plotAreaSpace.width ||
      y < plotAreaSpace.y ||
      y > plotAreaSpace.y + plotAreaSpace.height
    ) {
      return;
    }

    initialPointRef.current = { x, y };
  };

  const onPointerLeave = (event: PointerEvent) => {
    isHovered.current = false;
    if (dims && isDeviceMobile && mode != 'off') {
      (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    }
  };

  const onPointerMove = (event: PointerEvent) => {
    if (!isPointerDown) {
      return;
    }
    if (event.metaKey && initialPointRef.current) {
      initialPointRef.current.isMeta = true;
    }

    const x = event.offsetX;
    const y = event.offsetY;

    if (
      x < plotAreaSpace.x ||
      x > plotAreaSpace.x + plotAreaSpace.width ||
      y < plotAreaSpace.y ||
      y > plotAreaSpace.y + plotAreaSpace.height
    ) {
      return;
    }

    if (
      mode === 'pan' &&
      onViewportChange &&
      (!isDeviceMobile || (isDeviceMobile && numOfActiveTouches.current === 1))
    ) {
      const axisDelta =
        xScale.invert(x) - xScale.invert((finalPoint?.x || initialPointRef.current?.x)!);

      const viewportRange = viewportMax - viewportMin;

      let xMin = viewportMin - axisDelta;
      let xMax = viewportMax - axisDelta;

      if (xMin < xAxisExtent.min) {
        xMin = xAxisExtent.min;
        xMax = xMin + viewportRange;
      }

      if (xMax > xAxisExtent.max) {
        xMax = xAxisExtent.max;
        xMin = xMax - viewportRange;
      }

      if (xMin != viewportMin && xMax != viewportMax) {
        onViewportChange({
          xMin,
          xMax
        });
      }
    }

    setFinalPoint({ x, y });
  };

  const onPointerUp = (event: PointerEvent) => {
    setIsPointerDown(false);
    setFinalPoint(undefined);
    initialPointRef.current = undefined;

    if (dragButtonDivRef.current?.contains(event.target as HTMLElement)) {
      return;
    }

    if (!dims) {
      return;
    }

    const { x1, x2 } = dims;

    if (mode === 'zoom') {
      const xMin = Math.min(xScale.invert(x1), xScale.invert(x2));
      const xMax = Math.max(xScale.invert(x1), xScale.invert(x2));

      onViewportChange?.({
        xMin,
        xMax
      });
    }

    if (mode === 'select') {
      const ids = [
        ...new Set([
          ...selectedIdsRef.current,
          ...(event.metaKey ? selectedIds || new Set<K>() : new Set<K>())
        ])
      ];
      const data = ids.map((id) => getDataById(id));
      onSelectionChange?.({ ids, data });
      selectedIdsRef.current = new Set<K>();
    }
  };

  // if mode is 'off', we still want to show the toggleButton.
  // pointerUp will not fire because browser will have already claimed the
  // pointer
  const onTouchEnd = (event: TouchEvent) => {
    setIsPointerDown(false);
    setFinalPoint(undefined);
    initialPointRef.current = undefined;
    numOfActiveTouches.current = event.touches.length;
  };

  const onTouchStart = (event: TouchEvent) => {
    numOfActiveTouches.current = event.touches.length;
  };

  let marqueeContent: VNode | undefined;

  const isPointInsideMarquee =
    mode === 'select' && dims
      ? (id: K, point: { x: number; y: number }) => {
          if (initialPointRef.current?.isMeta && selectedIds?.has(id)) {
            return true;
          }
          const { x1, x2, y1, y2 } = dims;
          const { x, y } = point;
          if (x <= x2 && x >= x1 && y <= y2 && y >= y1) {
            selectedIdsRef.current?.add(id);
            return true;
          } else {
            selectedIdsRef.current?.delete(id);
          }
          return false;
        }
      : undefined;

  if (dims && (mode === 'zoom' || mode === 'select')) {
    marqueeContent = <Marquee {...dims} />;
  }

  return {
    cursor: getCursorType(mode, isPointerDown),
    marqueeContent,
    toggleButtonContent:
      !isPointerDown &&
      ((!isDeviceMobile && isHovered.current) || isDeviceMobile) &&
      toggleButtonContent,
    isPointInsideMarquee,
    dragMode: mode,
    marqueeProps:
      dragMode != 'off'
        ? {
            onPointerDown,
            onPointerMove,
            onPointerUp,
            onPointerLeave,
            onPointerEnter,
            onTouchEnd,
            onTouchStart
          }
        : {}
  };
}
